import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { canAccessPage } from "../utils/roleAccess";

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function MenuItemIcon({ type }) {
  if (type === "profile") {
    return (
      <svg viewBox="0 0 24 24">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
      </svg>
    );
  }

  if (type === "team") {
    return (
      <svg viewBox="0 0 24 24">
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M16 3.13a4 4 0 0 1 0 7.75M23 21v-2a4 4 0 0 0-3-3.87M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
      </svg>
    );
  }

  if (type === "logout") {
    return (
      <svg viewBox="0 0 24 24">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24">
      <path d="M12 3v2.25M12 18.75V21M4.93 4.93l1.59 1.59M17.48 17.48l1.59 1.59M3 12h2.25M18.75 12H21M4.93 19.07l1.59-1.59M17.48 6.52l1.59-1.59M12 15.75A3.75 3.75 0 1 0 12 8.25a3.75 3.75 0 0 0 0 7.5z" />
    </svg>
  );
}

export default function ProfileDropdown() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  if (!user) {
    return null;
  }

  const canOpenSettings = canAccessPage(user, "settings");
  const canOpenTeam = canAccessPage(user, "team-management");

  const handleNavigate = (nextPath) => {
    setOpen(false);
    navigate(nextPath);
  };

  return (
    <div
      ref={rootRef}
      className={`profile-dropdown ${open ? "open" : ""}`.trim()}
    >
      <button
        type="button"
        className="profile-dropdown-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="profile-chip-avatar">{user.avatarInitials || "AA"}</span>
        <span className="profile-chip-copy">
          <span className="profile-chip-name">{user.name}</span>
          <span className="profile-chip-role">{user.role}</span>
        </span>
        <span className="profile-dropdown-chevron">
          <ChevronIcon />
        </span>
      </button>

      <div className="profile-dropdown-menu" role="menu" aria-label="Profile menu">
        <div className="profile-dropdown-heading">
          <div className="profile-dropdown-name">{user.name}</div>
          <div className="profile-dropdown-email">{user.email}</div>
        </div>
        {canOpenSettings ? (
          <button
            type="button"
            className="profile-dropdown-item"
            onClick={() => handleNavigate("/settings")}
          >
            <span className="profile-dropdown-item-icon">
              <MenuItemIcon type="settings" />
            </span>
            Settings
          </button>
        ) : null}
        {canOpenTeam ? (
          <button
            type="button"
            className="profile-dropdown-item"
            onClick={() => handleNavigate("/team-management")}
          >
            <span className="profile-dropdown-item-icon">
              <MenuItemIcon type="team" />
            </span>
            Team Management
          </button>
        ) : null}
        <button
          type="button"
          className="profile-dropdown-item danger"
          onClick={async () => {
            setOpen(false);
            await logout();
            navigate("/login", { replace: true });
          }}
        >
          <span className="profile-dropdown-item-icon">
            <MenuItemIcon type="logout" />
          </span>
          Logout
        </button>
      </div>
    </div>
  );
}
